/**
 * ChangeTierDialog — modal para cambiar el tier de licencia de un usuario
 * ya activo en el tenant (OT-0020).
 *
 * Único flujo soportado para mover a alguien entre tiers: antes de esta
 * OT la única alternativa era un UPDATE manual contra la BD, sin
 * confirmación, sin invariantes y sin auditoría.
 *
 * El diálogo de confirmación reforzada para promover a `admin` reusa la
 * copy y el patrón de `InviteUserDialog` (checkbox `confirm_admin_label`)
 * — no se inventa otro (OT-0020 §4.5).
 *
 * Las 6 invariantes las hace cumplir el backend; este diálogo solo las
 * muestra cuando el backend las rechaza — incluida la lista de documentos
 * o proyectos que bloquean la degradación (§4.2 invariantes 5 y 6).
 */

import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { ApiError } from '../../api/client'
import { changeTenantUserTier, type TenantUserSummary } from '../../api/tenants'
import type { UserTenantRole } from '../../api/me'
import {
  TIERS,
  canSubmitTierChange,
  extractTierChangeBlockers,
  tierHelpKey,
  tierLabelKey,
} from '../../lib/tiers'
import { useAccessToken } from '../../auth/useAccessToken'
import { Dialog } from '../Dialog'
import { pushToast } from '../../stores/toastStore'

interface ChangeTierDialogProps {
  open: boolean
  onClose: () => void
  tenantId: string
  user: TenantUserSummary
}

export function ChangeTierDialog({
  open,
  onClose,
  tenantId,
  user,
}: ChangeTierDialogProps) {
  const { t } = useTranslation(['team', 'common'])
  const { accessToken } = useAccessToken()
  const queryClient = useQueryClient()

  const [tier, setTier] = useState<UserTenantRole>(user.role)
  const [confirmAdmin, setConfirmAdmin] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [blockedBy, setBlockedBy] = useState<string[] | null>(null)

  const mutation = useMutation({
    mutationFn: async () => {
      if (!accessToken) throw new Error('no_access_token')
      return changeTenantUserTier(accessToken, tenantId, user.id, {
        tier,
        confirm_admin: confirmAdmin,
      })
    },
    onSuccess: () => {
      pushToast({
        kind: 'success',
        message: t('change_tier_modal.success', {
          name: user.display_name,
          tier: t(tierLabelKey(tier)),
        }),
      })
      void queryClient.invalidateQueries({ queryKey: ['tenant-users', tenantId] })
      reset()
      onClose()
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setBlockedBy(extractTierChangeBlockers(err.body?.details))
        setError(err.body?.message ?? err.message)
      } else {
        setBlockedBy(null)
        setError(t('change_tier_modal.error_generic'))
      }
    },
  })

  const reset = () => {
    setTier(user.role)
    setConfirmAdmin(false)
    setError(null)
    setBlockedBy(null)
  }

  const handleClose = () => {
    if (mutation.isPending) return
    reset()
    onClose()
  }

  const submitDisabled =
    mutation.isPending || !canSubmitTierChange(user.role, tier, confirmAdmin)

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={t('change_tier_modal.title')}
      footer={
        <>
          <button
            type="button"
            onClick={handleClose}
            disabled={mutation.isPending}
            className="rounded-md border border-line bg-surface px-3 py-1.5 text-sm text-ink hover:bg-surface-muted disabled:opacity-50"
          >
            {t('common:actions.cancel')}
          </button>
          <button
            type="button"
            onClick={() => {
              setError(null)
              setBlockedBy(null)
              mutation.mutate()
            }}
            disabled={submitDisabled}
            className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-canvas hover:bg-brand/90 disabled:opacity-50"
          >
            {mutation.isPending
              ? t('change_tier_modal.submitting')
              : t('change_tier_modal.submit')}
          </button>
        </>
      }
    >
      <p className="font-body text-sm text-ink-muted mb-4">
        {t('change_tier_modal.subtitle', { name: user.display_name })}
      </p>

      <div className="space-y-4">
        <fieldset>
          <legend className="block text-xs font-medium text-ink mb-1">
            {t('change_tier_modal.tier_label')}
          </legend>
          <div className="space-y-1.5">
            {TIERS.map((value) => {
              const helpKey = tierHelpKey(value)
              return (
                <label
                  key={value}
                  className={`flex gap-2 items-start rounded-md border px-3 py-2 cursor-pointer transition-colors ${
                    tier === value
                      ? 'border-brand bg-brand-soft'
                      : 'border-line bg-surface hover:bg-surface-muted'
                  }`}
                >
                  <input
                    type="radio"
                    name="change-tier"
                    value={value}
                    checked={tier === value}
                    onChange={() => {
                      setTier(value)
                      if (value !== 'admin') setConfirmAdmin(false)
                    }}
                    disabled={mutation.isPending}
                    className="mt-1"
                  />
                  <span>
                    <span className="block text-sm font-medium text-ink">
                      {t(tierLabelKey(value))}
                      {value === user.role
                        ? ` — ${t('change_tier_modal.current_tier_suffix')}`
                        : ''}
                    </span>
                    <span className="block text-[11px] text-ink-muted mt-0.5">
                      {t(helpKey)}
                    </span>
                  </span>
                </label>
              )
            })}
          </div>
        </fieldset>

        {tier === 'admin' && (
          <label className="flex gap-2 items-start rounded-md border border-review/30 bg-review-soft px-3 py-2">
            <input
              type="checkbox"
              checked={confirmAdmin}
              onChange={(e) => setConfirmAdmin(e.target.checked)}
              disabled={mutation.isPending}
              className="mt-0.5"
            />
            <span className="text-xs text-ink">
              {t('invite_modal.confirm_admin_label')}
            </span>
          </label>
        )}

        {error && (
          <div
            role="alert"
            className="text-xs text-critical bg-critical-soft border border-critical/30 rounded-md px-3 py-2"
          >
            <p>{error}</p>
            {blockedBy && blockedBy.length > 0 && (
              <ul className="mt-1.5 list-disc list-inside space-y-0.5">
                {/* Los nombres se repiten entre proyectos por construcción
                    (las plantillas generan los mismos), así que el nombre
                    solo no sirve de key. La lista no se reordena. */}
                {blockedBy.map((name, index) => (
                  <li key={`${index}-${name}`}>{name}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </Dialog>
  )
}
